import type {
  CompletionLevel,
  EntryId,
  EntryKind,
  EntryMode,
  EventId,
  HabitId,
  GoalCategory,
  GoalId,
  MeasureId,
  MeasureShape,
  ReadingId,
  Schedule,
  SessionMode,
  TaskId,
} from './events.js';
import type { RankId, TitleId } from './ruleset.js';
import type { StudentPath } from './flavour.js';
import type { StudyDate } from './time.js';

/**
 * DERIVED STATE — a cache, never a source of truth.
 *
 * Everything here is reconstructible by folding the event log. It is
 * persisted only as a checkpoint so the app can paint the Home screen without
 * replaying thousands of events on cold start; if it is ever lost, corrupt,
 * or built by an older ruleset, it is thrown away and rebuilt.
 *
 * Plain `Record`s rather than `Map`s so a checkpoint is JSON without a
 * custom serialiser.
 */

export type Habit = {
  id: HabitId;
  name: string;
  schedule: Schedule;
  icon?: string;
  /**
   * The two-minute version, in the student's own words — "one page", "open the
   * book", "five problems".
   *
   * Absent for habits created before the floor existed, and for habits whose
   * owner never wrote one. A habit with no floor simply has no floor button:
   * the feature degrades to what shipped before it.
   */
  floor?: string;
  /**
   * What this habit follows — "Maghrib", "dinner", "getting home".
   *
   * An implementation intention: *after X, I will Y at Z*. The meta-analysis
   * behind it covers 642 tests at d = .27–.66, and the effect is larger when
   * the plan is in if-then form and rehearsed — which is why this is a slot in
   * a sentence rather than a free-text "notes" field. A cue the student can
   * actually notice beats a clock, and it survives a disrupted day (§53.1).
   */
  anchor?: string;
  /** Where it happens. The other half of the cue. */
  place?: string;
  /**
   * Which goal this habit serves, when it serves one.
   *
   * A TAG, not a tree. Goals do not contain habits — the habit names its goal
   * the same way an entry names its track, and a habit with no goal is an
   * ordinary habit rather than an orphan. That distinction is what keeps
   * multiple goals from turning into project management: there is nothing to
   * organise, no assignment screen, and no state where the data is wrong.
   */
  goalId?: GoalId;
  /**
   * Local hours at which this habit was recently completed, newest last.
   *
   * Kept so the app can PROPOSE an anchor instead of asking for one. A plan
   * typed at creation is a guess about a habit the student has not performed
   * yet; three real completions know when it actually happens. Confirming
   * "you usually do this after Maghrib" is one tap, and it is truer than
   * anything they would have typed on day one.
   *
   * Capped, because only the recent rhythm matters — a habit that has moved
   * from mornings to evenings should propose evenings.
   */
  recentHours: number[];
  archived: boolean;
  createdOn: StudyDate;
};

export type Task = {
  id: TaskId;
  name: string;
  track?: string;
  dueDate?: StudyDate;
  done: boolean;
  /** The day it was ticked, so today's finished work can stay on today's list
   *  and drop off when the day turns over. Absent whenever `done` is false. */
  completedOn?: StudyDate;
  archived: boolean;
  createdOn: StudyDate;
};

/**
 * What the student is working toward (§53.4).
 *
 * Modelled as WOOP — Wish, Outcome, Obstacle, Plan — because that is the
 * shape with the evidence behind it. `title` is the Wish and `outcome` is
 * what will be different on arrival; `obstacle` is the half that makes the
 * other three work, since a goal screen showing only the desired future is
 * counterproductive rather than merely incomplete (MCII). The Plan is not a
 * field here at all: it is the habit created alongside the goal, carrying the
 * if-then in its own `anchor` and `place`.
 *
 * `obstacle` is nonetheless OPTIONAL, and that is deliberate. Asked at minute
 * one it returns "laziness"; asked after the first missed day — "what got in
 * the way on Wednesday?" — it returns something true and specific, because
 * that is recall rather than introspection. Onboarding offers chips and a
 * skip; the weekly review is what actually fills this in.
 *
 * Progress is `parts`, never elapsed time and never a target score. Elapsed
 * time advances whether or not you work, and a score is the performance goal
 * that §43 says harms the student who is behind. Parts only move when work
 * happens, and they cannot be put into a wrong state by a bad week — miss
 * three days and the pace recomputes and states the truth.
 */
export type Goal = {
  id: GoalId;
  /** Which preloaded template this came from, or '' for a typed one. The
   *  HSC syllabus is fixed and public, so a preset can arrive with its whole
   *  chapter list already in `parts` (§53.4). */
  preset: string;
  /** The Wish. "HSC 2027 — Mathematics 85%+" */
  title: string;
  category: GoalCategory;
  /** What is different when you arrive. Motivational, never scored. */
  outcome?: string;
  /** What usually gets in the way. Filled in by evidence, not interrogation. */
  obstacle?: string;
  targetDate: StudyDate;
  /** How you will know it is finished, for a goal whose parts don't say so.
   *  A goal you can complete is a goal you can celebrate. */
  doneWhen?: string;
  /** The ordered chunks of actual work — chapters, modules, topics. Empty is
   *  normal and honest: a goal that cannot be decomposed has no progress bar,
   *  and inventing one would be a lie. */
  parts: string[];
  /** Which of `parts` are done. Ordered by completion, not by position. */
  partsCompleted: string[];
  tracks: string[];
  startedOn: StudyDate;
  status: 'active' | 'completed' | 'abandoned';
};

/** One record of work done — hand-typed or timer-produced, same shape. */
export type Entry = {
  id: EntryId;
  track: string;
  minutes: number;
  kind: EntryKind;
  mode: EntryMode;
  date: StudyDate;
  deleted: boolean;
  planMinutes?: number;
  timerMode?: SessionMode;
  intention?: string;
  nextStep?: string;
  recall?: string;
};

/** A thing worth recording a number for. See `MeasureDefined` in events.ts. */
export type Measure = {
  id: MeasureId;
  name: string;
  shape: MeasureShape;
  track?: string;
  outOf?: number;
  unit?: string;
};

export type Reading = {
  id: ReadingId;
  measureId: MeasureId;
  value: number;
  date: StudyDate;
  /** The session that produced it, when there was one. */
  entryId?: EntryId;
  /** Overrides the measure's denominator for this reading alone. */
  outOf?: number;
  deleted: boolean;
};

/**
 * Per-day counters. The window used for Titles is a sum over the last N of
 * these, so anything a title predicate needs must be counted here — that's
 * why the shape looks redundant with BehaviourWindow.
 */
export type DayStats = {
  date: StudyDate;
  active: boolean;
  habitsCompleted: number;
  tasksCompleted: number;
  tasksOpened: number;
  /** All logged minutes for the day, whichever way they were captured. */
  minutes: number;
  /** The `review` slice of `minutes`. */
  reviewMinutes: number;
  /** Entries that came from a running timer rather than being typed in. */
  timedSessions: number;
  pomodoroSessions: number;
  longSessions: number;
  lateActivities: number;
  earlyActivities: number;
  activities: number;
  /** XP from discrete completions. */
  xpFlat: number;
  /** XP from logged minutes, already clamped to the daily cap. */
  xpMinutes: number;
  /** Awarded in the finalise pass, once streaks are known. */
  xpStreak: number;
  /**
   * Awarded in the finalise pass to a day that restarted activity after a gap.
   *
   * Separate from `xpStreak` because it is the opposite reward: the streak
   * bonus pays for continuity, this pays for returning after continuity broke.
   * Keeping them apart is what lets the UI say honestly which one was earned.
   */
  xpComeback: number;
  /**
   * True when this day followed at least one missed day and there was earlier
   * activity to have broken. The north-star metric (§51) is a ratio over this
   * field, so it is stored rather than recomputed at read time.
   */
  comeback: boolean;
  /**
   * How many days were missed immediately before this one.
   *
   * Stored rather than derived because once the comeback is made this day
   * becomes the last active day, and the gap behind it is no longer
   * recoverable from `streak.lastActiveDate` — which is precisely when the UI
   * needs it. 0 on any day that is not a comeback.
   */
  missedBefore: number;
  /** How many of the day's completions were floors rather than full. */
  floorsKept: number;
};

export type DerivedState = {
  /** Which ruleset produced these numbers. A mismatch forces a full replay. */
  rulesetVersion: number;
  /** Last applied event id — the incremental cursor. */
  cursor: EventId | null;
  eventCount: number;

  profileName: string | null;
  /** What they said they are preparing for. Picks the register the app speaks
   *  in, and the pack their goal presets come from. */
  profilePath: StudentPath | null;

  habits: Record<HabitId, Habit>;
  /** Display order, including archived ids. Object key order is not a
   *  contract, so the list the student arranged gets its own field. */
  habitOrder: HabitId[];
  tasks: Record<TaskId, Task>;
  goals: Record<GoalId, Goal>;
  entries: Record<EntryId, Entry>;
  measures: Record<MeasureId, Measure>;
  readings: Record<ReadingId, Reading>;

  /**
   * Keyed `habitId|studyDate`; the latest complete/uncomplete event wins.
   *
   * The value is the LEVEL, not `true` — a floor day and a full day are both
   * kept promises and both keep the streak, but they are not the same fact and
   * the grid has to be able to show the difference.
   */
  completions: Record<string, CompletionLevel>;

  days: Record<StudyDate, DayStats>;
  tracks: Record<string, { minutes: number; timedSessions: number }>;

  totals: {
    minutes: number;
    reviewMinutes: number;
    timedSessions: number;
    habitsCompleted: number;
    tasksCompleted: number;
    goalsStarted: number;
    goalsCompleted: number;
  };

  /** Materialised from the log, never recomputed away (spec §11). */
  achievements: Record<string, { unlockedAt: number }>;
  ranksReached: Record<string, number>;

  // ── finalise pass output ────────────────────────────────────────────────
  power: number;
  rank: RankId;
  rankRatio: number;
  titles: TitleId[];
  streak: { current: number; longest: number; lastActiveDate: StudyDate | null };
};

export const completionKey = (habitId: HabitId, date: StudyDate) => `${habitId}|${date}`;

/**
 * Apply a reorder to the list we already hold.
 *
 * Ids `next` doesn't mention keep their relative order at the end; ids it
 * mentions that we've never seen are ignored. So a list written on a phone
 * that hasn't synced a newly-created habit can't lose or duplicate it.
 */
export function applyOrder(current: readonly HabitId[], next: readonly HabitId[]): HabitId[] {
  const known = new Set(current);
  const moved = next.filter((id) => known.has(id));
  const placed = new Set(moved);
  return [...moved, ...current.filter((id) => !placed.has(id))];
}

/**
 * Habits in display order — the one definition every list reads.
 *
 * Falls back to insertion order for anything the order list misses, which is
 * what a checkpoint written before this field existed looks like.
 */
export function orderedHabits(state: DerivedState): Habit[] {
  const seen = new Set<HabitId>();
  const ordered: Habit[] = [];
  for (const id of state.habitOrder) {
    const habit = state.habits[id];
    if (habit) {
      ordered.push(habit);
      seen.add(id);
    }
  }
  for (const habit of Object.values(state.habits)) if (!seen.has(habit.id)) ordered.push(habit);
  return ordered;
}

export function emptyDay(date: StudyDate): DayStats {
  return {
    date,
    active: false,
    habitsCompleted: 0,
    tasksCompleted: 0,
    tasksOpened: 0,
    minutes: 0,
    reviewMinutes: 0,
    timedSessions: 0,
    pomodoroSessions: 0,
    longSessions: 0,
    lateActivities: 0,
    earlyActivities: 0,
    activities: 0,
    xpFlat: 0,
    xpMinutes: 0,
    xpStreak: 0,
    xpComeback: 0,
    comeback: false,
    missedBefore: 0,
    floorsKept: 0,
  };
}

export function initialState(rulesetVersion: number): DerivedState {
  return {
    rulesetVersion,
    cursor: null,
    eventCount: 0,
    profileName: null,
    profilePath: null,
    habits: {},
    habitOrder: [],
    tasks: {},
    goals: {},
    entries: {},
    measures: {},
    readings: {},
    completions: {},
    days: {},
    tracks: {},
    totals: {
      minutes: 0,
      reviewMinutes: 0,
      timedSessions: 0,
      habitsCompleted: 0,
      tasksCompleted: 0,
      goalsStarted: 0,
      goalsCompleted: 0,
    },
    achievements: {},
    ranksReached: {},
    power: 0,
    rank: 'novice',
    rankRatio: 0,
    titles: [],
    streak: { current: 0, longest: 0, lastActiveDate: null },
  };
}
